import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { itemStatusService } from "../services/itemStatusService";
import {
  BulkUpdateItemStatusRequest,
  CreateItemStatusRequest,
  ItemStatus,
  ItemStatusFilters,
  ReorderItemStatusesRequest,
  UpdateItemStatusRequest,
} from "../types/admin";

// Query keys
const ITEM_STATUS_KEYS = {
  all: ["itemStatuses"] as const,
  lists: () => [...ITEM_STATUS_KEYS.all, "list"] as const,
  list: (filters: ItemStatusFilters) =>
    [...ITEM_STATUS_KEYS.lists(), filters] as const,
  details: () => [...ITEM_STATUS_KEYS.all, "detail"] as const,
  detail: (id: number) => [...ITEM_STATUS_KEYS.details(), id] as const,
  statistics: () => [...ITEM_STATUS_KEYS.all, "statistics"] as const,
};

// Hook for fetching item statuses with filters
export const useItemStatuses = (filters: ItemStatusFilters) => {
  return useQuery({
    queryKey: ITEM_STATUS_KEYS.list(filters),
    queryFn: () => itemStatusService.getItemStatusesWithFilters(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for fetching item status statistics
export const useItemStatusStatistics = () => {
  return useQuery({
    queryKey: ITEM_STATUS_KEYS.statistics(),
    queryFn: () => itemStatusService.getItemStatusStatistics(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for fetching single item status
export const useItemStatus = (id: number) => {
  return useQuery({
    queryKey: ITEM_STATUS_KEYS.detail(id),
    queryFn: () => itemStatusService.getItemStatusDetail(id),
    enabled: !!id,
  });
};

// Hook for item status mutations
export const useItemStatusMutations = () => {
  const queryClient = useQueryClient();

  const createStatus = useMutation({
    mutationFn: (data: CreateItemStatusRequest) =>
      itemStatusService.createItemStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: ITEM_STATUS_KEYS.statistics(),
      });
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateItemStatusRequest }) =>
      itemStatusService.updateItemStatus(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.detail(id) });
      queryClient.invalidateQueries({
        queryKey: ITEM_STATUS_KEYS.statistics(),
      });
    },
  });

  const deleteStatus = useMutation({
    mutationFn: (id: number) => itemStatusService.deleteItemStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: ITEM_STATUS_KEYS.statistics(),
      });
    },
  });

  const toggleStatusActive = useMutation({
    mutationFn: (id: number) => itemStatusService.toggleItemStatusActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: ITEM_STATUS_KEYS.statistics(),
      });
    },
  });

  const bulkUpdateStatusActive = useMutation({
    mutationFn: (data: BulkUpdateItemStatusRequest) =>
      itemStatusService.bulkUpdateItemStatusActive(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: ITEM_STATUS_KEYS.statistics(),
      });
    },
  });

  const reorderStatuses = useMutation({
    mutationFn: (data: ReorderItemStatusesRequest) =>
      itemStatusService.reorderItemStatuses(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ITEM_STATUS_KEYS.lists() });
    },
  });

  return {
    createStatus,
    updateStatus,
    deleteStatus,
    toggleStatusActive,
    bulkUpdateStatusActive,
    reorderStatuses,
  };
};

// Hook for optimistic updates
export const useOptimisticItemStatusUpdate = () => {
  const queryClient = useQueryClient();

  const optimisticUpdate = useCallback(
    (statusId: number, updates: Partial<ItemStatus>) => {
      // Update all lists that might contain this status
      queryClient.setQueriesData(
        { queryKey: ITEM_STATUS_KEYS.lists() },
        (oldData: any) => {
          if (!oldData?.data?.statuses) return oldData;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              statuses: oldData.data.statuses.map((status: ItemStatus) =>
                status.id === statusId ? { ...status, ...updates } : status,
              ),
            },
          };
        },
      );

      // Update the specific status detail
      queryClient.setQueryData(
        ITEM_STATUS_KEYS.detail(statusId),
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: { ...oldData.data, ...updates },
          };
        },
      );
    },
    [queryClient],
  );

  return { optimisticUpdate };
};

// Hook for getting all item statuses (for dropdowns, etc.)
export const useAllItemStatuses = () => {
  return useQuery({
    queryKey: [...ITEM_STATUS_KEYS.all, "all"],
    queryFn: () => itemStatusService.getAllItemStatuses(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for getting active item statuses only
export const useActiveItemStatuses = () => {
  return useQuery({
    queryKey: [...ITEM_STATUS_KEYS.all, "active"],
    queryFn: () => itemStatusService.getActiveItemStatuses(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
